'use client';

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import Pagination from '../../organisms/Pagination/Pagination';
import Typography from '../../atoms/Typography/Typography';
import Box from '../../atoms/Box/Box';
import Header from './Header/Header';
import Row from './Row/Row';
import Toolbar from './Toolbar/Toolbar';
import { ManageColumns } from './Panel/ManageColumns/ManageColumns';
import { Filter } from './Panel/Filter/Filter';
import { TableProps, SortDirection, FilterState, TableColumn } from './types';
import { sortData, paginateData, convertToCSV, downloadCSV } from './utils';
import styles from './Table.module.scss';

/**
 * Applies client-side filtering to data based on filter state
 */
const applyFilters = <T extends Record<string, unknown>>(
  data: T[],
  filterState: FilterState,
  columns: TableColumn<T>[]
): T[] => {
  if (Object.keys(filterState).length === 0) return data;

  return data.filter((row) => {
    return Object.entries(filterState).every(([columnKey, filterValue]) => {
      if (filterValue === null || filterValue === undefined) return true;

      const column = columns.find((col) => col.key === columnKey);
      if (!column?.filter) return true;

      const cellValue = row[columnKey];
      const filterType = column.filter.type;

      switch (filterType) {
        case 'text': {
          const searchValue = (filterValue as string).toLowerCase();
          const cellString = String(cellValue ?? '').toLowerCase();
          return cellString.includes(searchValue);
        }

        case 'select': {
          return cellValue === filterValue;
        }

        case 'multiselect': {
          const selectedValues = filterValue as string[];
          if (selectedValues.length === 0) return true;
          return selectedValues.includes(String(cellValue));
        }

        case 'number': {
          const numValue = Number(cellValue);
          if (isNaN(numValue)) return false;
          const range = filterValue as { from?: number; to?: number };
          if (range.from !== undefined && numValue < range.from) return false;
          if (range.to !== undefined && numValue > range.to) return false;
          return true;
        }

        case 'date':
        case 'dateRange': {
          const cellDate = String(cellValue);
          if (typeof filterValue === 'string') {
            return cellDate === filterValue;
          }
          const dateRange = filterValue as { from?: string; to?: string };
          if (dateRange.from && cellDate < dateRange.from) return false;
          if (dateRange.to && cellDate > dateRange.to) return false;
          return true;
        }

        default:
          return true;
      }
    });
  });
};

const Table = <T extends Record<string, unknown>>({
  columns,
  data,
  theme = 'dark',
  className = '',
  dataTestId,
  compact = false,
  pagination,
  selection,
  search,
  sort,
  filter,
  columnConfig,
  emptyState,
  toolbar,
  labels,
}: TableProps<T>) => {
  // Destructure grouped props with defaults
  const selectable = selection?.enabled ?? true;
  const selectedRows = useMemo(() => selection?.rows ?? [], [selection?.rows]);
  const onSelectionChange = selection?.onChange;
  const onExport = selection?.export?.onExport;
  const exportButtonLabel = selection?.export?.label ?? 'Export';

  const searchable = !!search;
  const searchPlaceholder = search?.placeholder;
  const searchValue = search?.value;
  const onSearchChange = search?.onChange;

  const onSortProp = sort?.onSort;
  const sortColumnProp = sort?.column;
  const sortDirectionProp = sort?.direction;

  const showFilter = filter?.enabled ?? false;
  const filterContent = filter?.content;
  const controlledFilterState = filter?.state;
  const onFilterChange = filter?.onChange;
  const onPendingFilterChange = filter?.onPendingChange;
  const dependentFilters = filter?.dependentFilters;

  const columnsConfigurable = columnConfig?.enabled ?? false;
  const controlledColumnVisibility = columnConfig?.visibility;
  const onColumnVisibilityChange = columnConfig?.onChange;

  const emptyStateMessage = emptyState?.message ?? 'No data available';
  const filteredEmptyStateMessage =
    emptyState?.filteredMessage ?? 'No results found. Try adjusting your filters.';
  const renderFilteredEmptyState = emptyState?.renderFiltered;

  const toolbarActions = toolbar?.actions;
  const toolbarContent = toolbar?.content;
  const toolbarSubContent = toolbar?.subContent;

  const [internalSortColumn, setInternalSortColumn] = useState<string | null>(null);
  const [internalSortDirection, setInternalSortDirection] = useState<SortDirection>(null);

  // Panel open states
  const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  // Internal filter state (used when uncontrolled)
  const [internalFilterState, setInternalFilterState] = useState<FilterState>({});

  // Internal column visibility state (used when uncontrolled)
  const [internalColumnVisibility, setInternalColumnVisibility] = useState<Record<string, boolean>>(
    () => {
      const initialVisibility: Record<string, boolean> = {};
      columns.forEach((col) => {
        initialVisibility[col.key] = col.visible !== false;
      });
      return initialVisibility;
    }
  );

  // Determine if filtering/column visibility is controlled
  const isControlledFiltering = controlledFilterState !== undefined && onFilterChange !== undefined;
  const isControlledColumnVisibility =
    controlledColumnVisibility !== undefined && onColumnVisibilityChange !== undefined;

  // Active filter and column visibility states
  const filterState = isControlledFiltering ? controlledFilterState : internalFilterState;
  const columnVisibility = isControlledColumnVisibility
    ? controlledColumnVisibility
    : internalColumnVisibility;

  // Handlers for filter and column visibility changes
  const handleFilterChange = useCallback(
    (filters: FilterState) => {
      if (isControlledFiltering && onFilterChange) {
        onFilterChange(filters);
      } else {
        setInternalFilterState(filters);
      }
    },
    [isControlledFiltering, onFilterChange]
  );

  const handleColumnVisibilityChange = useCallback(
    (visibility: Record<string, boolean>) => {
      if (isControlledColumnVisibility && onColumnVisibilityChange) {
        onColumnVisibilityChange(visibility);
      } else {
        setInternalColumnVisibility(visibility);
      }
    },
    [isControlledColumnVisibility, onColumnVisibilityChange]
  );

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    return Object.values(filterState).filter((v) => v !== null && v !== undefined).length;
  }, [filterState]);

  // Filter visible columns
  const visibleColumns = useMemo(() => {
    return columns.filter((col) => columnVisibility[col.key] !== false);
  }, [columns, columnVisibility]);

  // Use controlled sort values if provided (server-side sorting), otherwise use internal state
  const isServerSideSorting = onSortProp !== undefined;
  const sortColumn = isServerSideSorting ? (sortColumnProp ?? null) : internalSortColumn;
  const sortDirection = isServerSideSorting ? (sortDirectionProp ?? null) : internalSortDirection;
  const [internalCurrentPage, setInternalCurrentPage] = useState(0);
  const initialPageSize = pagination?.enabled ? (pagination.pageSize ?? 10) : 10;
  const [internalPageSize, setInternalPageSize] = useState(initialPageSize);

  // Page size can be controlled (when onPageSizeChange is provided) or uncontrolled
  const isControlledPageSize = pagination?.enabled && pagination.onPageSizeChange !== undefined;
  const pageSize = isControlledPageSize ? (pagination.pageSize ?? 10) : internalPageSize;

  const [internalSelectedRows, setInternalSelectedRows] = useState<number[]>(
    Array.isArray(selectedRows) ? selectedRows : []
  );

  // Clear selected rows when data changes (e.g., due to search or page change with server-side pagination)
  useEffect(() => {
    if (!onSelectionChange) {
      setInternalSelectedRows([]);
    }
  }, [data, onSelectionChange]);

  // Use external currentPage if provided (server-side pagination), otherwise use internal state
  const isServerSidePagination =
    pagination?.enabled && pagination.current !== undefined && pagination.onChange !== undefined;
  const currentPage: number = isServerSidePagination
    ? (pagination.current ?? 0)
    : internalCurrentPage;

  const selectedIndices = useMemo(() => {
    if (onSelectionChange) {
      return Array.isArray(selectedRows) ? selectedRows : [];
    }
    return internalSelectedRows;
  }, [onSelectionChange, selectedRows, internalSelectedRows]);

  const handleSelectionChange = (indices: number[]) => {
    if (onSelectionChange) {
      onSelectionChange(indices);
    } else {
      setInternalSelectedRows(indices);
    }
  };

  const handleSort = (columnKey: string) => {
    const newDirection =
      sortColumn === columnKey ? (sortDirection === 'asc' ? 'desc' : 'asc') : 'asc';

    if (isServerSideSorting && onSortProp) {
      // Server-side sorting: call the callback
      onSortProp(columnKey, newDirection);
    } else {
      // Client-side sorting: update internal state
      if (sortColumn === columnKey) {
        setInternalSortDirection(newDirection);
      } else {
        setInternalSortColumn(columnKey);
        setInternalSortDirection('asc');
      }
    }
  };

  const sortedData = useMemo(() => {
    // Apply client-side filtering first (skip if server-side filtering)
    const filteredData = isControlledFiltering ? data : applyFilters(data, filterState, columns);

    // Skip client-side sorting if server-side sorting is enabled (data is already sorted)
    if (isServerSideSorting) {
      return filteredData;
    }
    return sortData(filteredData, sortColumn, sortDirection);
  }, [
    data,
    sortColumn,
    sortDirection,
    isServerSideSorting,
    filterState,
    isControlledFiltering,
    columns,
  ]);

  const paginatedData = useMemo(() => {
    // For server-side pagination, data is already paginated by the API
    if (isServerSidePagination) {
      return sortedData;
    }
    return paginateData(sortedData, pagination?.enabled || false, currentPage, pageSize);
  }, [sortedData, currentPage, pageSize, isServerSidePagination, pagination?.enabled]);

  const totalItems =
    isServerSidePagination && pagination.total !== undefined ? pagination.total : sortedData.length;

  const totalPages = useMemo(() => {
    if (!pagination?.enabled) return 1;
    // For server-side pagination, calculate from total prop
    if (isServerSidePagination && pagination.total !== undefined) {
      return Math.ceil(pagination.total / pageSize);
    }
    return Math.ceil(sortedData.length / pageSize);
  }, [sortedData.length, pageSize, isServerSidePagination, pagination]);

  const isAllSelected = useMemo(() => {
    if (paginatedData.length === 0) return false;
    const startIndex = pagination?.enabled ? currentPage * pageSize : 0;
    return paginatedData.every((_, idx) => selectedIndices.includes(startIndex + idx));
  }, [paginatedData, selectedIndices, pagination?.enabled, currentPage, pageSize]);

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIndices = paginatedData.map((_, idx) => {
        const startIndex = pagination?.enabled ? currentPage * pageSize : 0;
        return startIndex + idx;
      });
      handleSelectionChange(allIndices);
    } else {
      handleSelectionChange([]);
    }
  };

  const handleRowSelect = (index: number, checked: boolean) => {
    if (checked) {
      handleSelectionChange([...selectedIndices, index]);
    } else {
      handleSelectionChange(selectedIndices.filter((i) => i !== index));
    }
  };

  const handlePageChange = (page: number) => {
    if (isServerSidePagination && pagination.onChange) {
      pagination.onChange(page, pageSize);
    } else {
      setInternalCurrentPage(page);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    // Update internal state for uncontrolled mode
    if (!isControlledPageSize) {
      setInternalPageSize(newPageSize);
    }
    // Call external handler if provided
    if (pagination?.enabled && pagination.onPageSizeChange) {
      pagination.onPageSizeChange(newPageSize);
    }
    // Reset to first page when page size changes
    if (isServerSidePagination && pagination.onChange) {
      pagination.onChange(0, newPageSize);
    } else {
      setInternalCurrentPage(0);
    }
  };

  const handleExport = () => {
    // For server-side pagination, use sortedData since that's what's rendered
    // Convert absolute indices to relative indices since data only contains current page items
    const selectedData = selectedIndices
      .map((index) => {
        if (isServerSidePagination) {
          const relativeIndex = index - currentPage * pageSize;
          return sortedData[relativeIndex];
        }
        return sortedData[index];
      })
      .filter(Boolean);

    if (onExport) {
      onExport(selectedData);
    } else {
      const csvContent = convertToCSV(selectedData, columns);
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `export-${timestamp}.csv`;
      downloadCSV(csvContent, filename);
    }
  };

  return (
    <Box
      className={classnames(styles.tableWrapper, className, {
        [styles.compact]: compact,
      })}
      data-theme={theme}
      data-testid={dataTestId}
    >
      <Box
        className={classnames(styles.contentArea, {
          [styles.hasPanels]: columnsConfigurable || showFilter,
        })}
      >
        {/* Manage Columns Panel */}
        {columnsConfigurable && (
          <ManageColumns
            isOpen={isManageColumnsOpen}
            onClose={() => setIsManageColumnsOpen(false)}
            columns={columns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={handleColumnVisibilityChange}
            theme={theme}
            dataTestId="table-manage-columns-panel"
            labels={{
              title: labels?.manageColumns,
              selectAll: labels?.selectAll,
              clearAll: labels?.clearAll,
              columnsVisible: labels?.columnsVisible,
              save: labels?.save,
            }}
          />
        )}

        {/* Filter Panel */}
        {showFilter && (
          <Filter
            isOpen={isFilterOpen}
            onClose={() => setIsFilterOpen(false)}
            columns={columns}
            filterState={filterState}
            onFilterChange={handleFilterChange}
            onPendingFilterChange={onPendingFilterChange}
            dependentFilters={dependentFilters}
            theme={theme}
            dataTestId="table-filter-panel"
            filterContent={filterContent}
            labels={{
              title: labels?.filtersTitle,
              apply: labels?.apply,
              clearAll: labels?.clearAll,
            }}
          />
        )}

        {(selectable ||
          searchable ||
          toolbarActions?.length ||
          toolbarContent ||
          toolbarSubContent ||
          columnsConfigurable ||
          showFilter) && (
          <Toolbar
            theme={theme}
            export={
              selectable
                ? {
                    label: exportButtonLabel,
                    disabled: selectedIndices.length === 0,
                    onClick: handleExport,
                  }
                : undefined
            }
            search={
              searchable
                ? {
                    placeholder: searchPlaceholder,
                    value: searchValue,
                    onChange: onSearchChange,
                  }
                : undefined
            }
            filter={
              showFilter
                ? {
                    onClick: () => {
                      setIsFilterOpen((prev) => !prev);
                      setIsManageColumnsOpen(false);
                    },
                    activeCount: activeFilterCount,
                    state: filterState,
                    columns: columns as TableColumn<Record<string, unknown>>[],
                    onChange: handleFilterChange,
                  }
                : undefined
            }
            manageColumns={
              columnsConfigurable
                ? {
                    onClick: () => {
                      setIsManageColumnsOpen((prev) => !prev);
                      setIsFilterOpen(false);
                    },
                  }
                : undefined
            }
            actions={toolbarActions}
            content={toolbarContent}
            subContent={toolbarSubContent}
            labels={{
              manageColumns: labels?.manageColumns,
              filter: labels?.filter,
              filterWithCount: labels?.filterWithCount,
              searchPlaceholder: labels?.searchPlaceholder,
              clearAll: labels?.clearAll,
            }}
          />
        )}

        <Box className={styles.tableArea}>
          <Box className={styles.tableContainer}>
            <table className={styles.table}>
              <Header
                columns={visibleColumns}
                theme={theme}
                selectable={selectable}
                isAllSelected={isAllSelected}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                onSelectAll={handleSelectAll}
              />
              <tbody className={styles.tableBody}>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan={visibleColumns.length + (selectable ? 1 : 0)}>
                      <Box pt={48} pb={48} className={styles.emptyState}>
                        {activeFilterCount > 0 ? (
                          renderFilteredEmptyState ? (
                            renderFilteredEmptyState()
                          ) : (
                            <Typography
                              variant="body"
                              size="large"
                              theme={theme}
                              color="gray-5"
                              align="center"
                            >
                              {filteredEmptyStateMessage}
                            </Typography>
                          )
                        ) : (
                          <Typography
                            variant="body"
                            size="large"
                            theme={theme}
                            color="gray-5"
                            align="center"
                          >
                            {emptyStateMessage}
                          </Typography>
                        )}
                      </Box>
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((record, rowIndex) => {
                    const actualIndex = pagination?.enabled
                      ? currentPage * pageSize + rowIndex
                      : rowIndex;
                    const isSelected = selectedIndices.includes(actualIndex);

                    return (
                      <Row
                        key={rowIndex}
                        record={record}
                        rowIndex={rowIndex}
                        actualIndex={actualIndex}
                        columns={visibleColumns}
                        theme={theme}
                        selectable={selectable}
                        isSelected={isSelected}
                        onRowSelect={handleRowSelect}
                      />
                    );
                  })
                )}
              </tbody>
            </table>
          </Box>
        </Box>
      </Box>
      {pagination?.enabled && paginatedData.length > 0 && (
        <Box
          className={styles.paginationContainer}
          theme={theme}
          testId="table-pagination-container"
        >
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            theme={theme}
            pageSize={pageSize}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={pagination.pageSizeOptions}
            totalItems={totalItems}
            labels={{
              showingItems: labels?.showingItems,
              pageOf: labels?.pageOf,
            }}
          />
        </Box>
      )}
    </Box>
  );
};

Table.displayName = 'Table';

export default Table as <T extends Record<string, unknown>>(
  props: TableProps<T>
) => React.JSX.Element;
